// resources/js/webpush.js

import { buildDefaultHeaders } from './native/push'

// Hilfsfunktion, um den VAPID Public Key von Base64URL nach Uint8Array zu konvertieren
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function isWebPushSupported() {
  return ('serviceWorker' in navigator) && ('PushManager' in window)
}

async function sendSubscriptionToBackend(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include', // Session-Cookie mitsenden
    headers: buildDefaultHeaders(),
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    throw new Error(`WebPush backend call failed with status ${res.status} (${res.url})`)
  }
}

/**
 * Registriert den aktuellen Browser/Client für WebPush.
 *
 * @returns {Promise<'registered'|'denied'|'blocked'|'unsupported'>} 'denied' =
 *   Nutzer muss Benachrichtigungen in den Browser-Einstellungen erlauben,
 *   'blocked' = der Browser blockiert den Push-Dienst selbst (z. B. Brave ohne
 *   Google-Push-Dienste). Systemische Fehler (fehlender VAPID-Key, Backend-Fehler)
 *   werfen eine Exception.
 */
export async function registerWebPush() {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }

  // Warten, bis der Service Worker bereit ist
  const reg = await navigator.serviceWorker.ready

  // Bereits vorhandene Subscription erneut ans Backend melden (idempotent) –
  // deckt den Fall ab, dass die Subscription serverseitig verloren ging.
  const existingSub = await reg.pushManager.getSubscription()
  if (existingSub) {
    await sendSubscriptionToBackend('/user/webpush/subscribe', existingSub.toJSON())
    return 'registered'
  }

  // Browser-Permission einholen
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return 'denied'
  }

  if (!window.vapidPubKey) {
    throw new Error('VITE_VAPID_PUBLIC_KEY is not set')
  }

  const appKey = urlBase64ToUint8Array(window.vapidPubKey)

  let subscription
  try {
    // Subscription beim Push-Dienst anlegen
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey
    })
  } catch (e) {
    // NotAllowedError = Nutzer/Browser-Einstellung; AbortError/"push service error" =
    // der Browser blockiert den Push-Dienst (typisch Brave ohne Google-Push-Dienste).
    if (e?.name === 'NotAllowedError' || /denied/i.test(String(e?.message))) {
      return 'denied'
    }
    if (e?.name === 'AbortError' || /push service error/i.test(String(e?.message))) {
      return 'blocked'
    }
    throw e
  }

  await sendSubscriptionToBackend('/user/webpush/subscribe', subscription.toJSON())

  return 'registered'
}

/**
 * Abmelden vom WebPush. Systemische Fehler werfen eine Exception.
 *
 * @returns {Promise<'unregistered'|'unsupported'>}
 */
export async function unregisterWebPush() {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }

  const reg = await navigator.serviceWorker.ready
  const subscription = await reg.pushManager.getSubscription()
  if (!subscription) {
    return 'unregistered'
  }

  // Backend informieren
  await sendSubscriptionToBackend('/user/webpush/unsubscribe', { endpoint: subscription.toJSON().endpoint })

  // Lokale Subscription kündigen
  await subscription.unsubscribe()

  return 'unregistered'
}
